import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "../components/SEO";
import TimeSlotsEditor from "../components/TimeSlotsEditor";
import { emptyTime, isSlotFilled, isSlotWideEnough, buildProposedTimes, MIN_SESSION_MINUTES } from "../utils/guidanceTimes";
import "../Styles/GuidancePage.css";
import "../Styles/GuidanceRespond.css";

function fmtDateTime(t) {
  if (!t) return "";
  const d = new Date(t.date + "T00:00:00");
  const dateStr = d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const [h, m] = String(t.time || "").split(":").map(Number);
  const timeStr = Number.isFinite(h) && Number.isFinite(m)
    ? `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "pm" : "am"}`
    : t.time;
  return `${dateStr} at ${timeStr}`;
}

const MIN_TIMES = 2;
const MAX_TIMES = 3;

export default function GuidanceRespond() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get("id");
  const token = params.get("token");

  const [view, setView] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [chosenIdx, setChosenIdx] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [justConfirmed, setJustConfirmed] = useState(null);

  const [times, setTimes] = useState([emptyTime(), emptyTime()]);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState("");
  const [justResubmitted, setJustResubmitted] = useState(false);

  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState("");

  const load = useCallback(async () => {
    if (!id || !token) { setLoadError("This link is missing some details."); return; }
    try {
      const res = await fetch(`/api/guidance/status?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "This link is no longer valid.");
      setView(data);
      setLoadError("");
    } catch (err) {
      setLoadError(err.message || "This link is no longer valid.");
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const requestReschedule = async () => {
    setRescheduling(true);
    setRescheduleError("");
    try {
      const res = await fetch("/api/guidance/reschedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not process — please try again.");
      setJustConfirmed(null);
      await load();
    } catch (err) {
      setRescheduleError(err.message);
    }
    setRescheduling(false);
  };

  const confirmTime = async () => {
    if (chosenIdx === null) return;
    setConfirming(true);
    setConfirmError("");
    try {
      const chosenTime = view.offeredTimes[chosenIdx];
      const res = await fetch("/api/guidance/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, token, chosenTime }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not confirm — please try again.");
      setJustConfirmed(data.confirmedTime || chosenTime);
    } catch (err) {
      setConfirmError(err.message);
    }
    setConfirming(false);
  };

  const resubmit = async (e) => {
    e.preventDefault();
    const filled = times.filter(isSlotFilled);
    if (filled.length < MIN_TIMES) {
      setResubmitError(`Please suggest at least ${MIN_TIMES} possible times.`);
      return;
    }
    if (filled.some(t => !isSlotWideEnough(t))) {
      setResubmitError(`Each range needs to be at least ${MIN_SESSION_MINUTES} minutes so a full session fits inside it.`);
      return;
    }
    setResubmitting(true);
    setResubmitError("");
    try {
      const res = await fetch("/api/guidance/resubmit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, token, proposedTimes: buildProposedTimes(filled, MAX_TIMES) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not send — please try again.");
      setJustResubmitted(true);
    } catch (err) {
      setResubmitError(err.message);
    }
    setResubmitting(false);
  };

  const renderBody = () => {
    if (loadError) {
      return (
        <div className="gr-card gr-error-card">
          <span className="gr-icon">✕</span>
          <h1>Link not found</h1>
          <p>{loadError}</p>
          <a href="/guidance" className="gp-submit-btn" style={{ display: "inline-block", textDecoration: "none" }}>
            Back to guidance sessions
          </a>
        </div>
      );
    }

    if (!view) {
      return <div className="gr-card"><p>Loading…</p></div>;
    }

    if (view.status === "confirmed" || justConfirmed) {
      const t = justConfirmed || view.confirmedTime;
      return (
        <div className="gr-card">
          <span className="gr-icon gr-icon-ok">✓</span>
          <h1>You're all set!</h1>
          <p>Your session with Pooja is confirmed for:</p>
          <p className="gr-time-highlight">{fmtDateTime(t)}</p>
          <p>Keep an eye on your inbox — a confirmation email is on its way.</p>
          {rescheduleError && <p className="gp-error">{rescheduleError}</p>}
          <button type="button" className="gr-reschedule-link" disabled={rescheduling} onClick={requestReschedule}>
            {rescheduling ? "One moment…" : "Can't make it? Request a different time"}
          </button>
        </div>
      );
    }

    if (view.status === "offered") {
      return (
        <div className="gr-card">
          <span className="gr-icon">📅</span>
          <h1>Pooja is available!</h1>
          <p>Pick whichever of these works for you — it'll be locked in immediately.</p>
          <div className="gr-options">
            {view.offeredTimes.map((t, i) => (
              <button
                type="button"
                key={i}
                className={`gr-option${chosenIdx === i ? " selected" : ""}`}
                onClick={() => setChosenIdx(i)}
              >
                {fmtDateTime(t)}
              </button>
            ))}
          </div>
          {confirmError && <p className="gp-error">{confirmError}</p>}
          <button type="button" className="gp-submit-btn" disabled={chosenIdx === null || confirming} onClick={confirmTime}>
            {confirming ? "Confirming…" : "Confirm this time →"}
          </button>
        </div>
      );
    }

    if (view.status === "declined") {
      if (justResubmitted) {
        return (
          <div className="gr-card">
            <span className="gr-icon gr-icon-ok">✓</span>
            <h1>New times sent</h1>
            <p>Pooja will take another look and get back to you.</p>
          </div>
        );
      }
      return (
        <div className="gr-card">
          <span className="gr-icon">🗓</span>
          <h1>None of those times work</h1>
          {view.declineReason && <p className="gr-reason">"{view.declineReason}"</p>}
          <p>No problem — suggest a couple more times below.</p>
          <form className="gp-form" onSubmit={resubmit} noValidate>
            <TimeSlotsEditor times={times} setTimes={setTimes} minTimes={MIN_TIMES} maxTimes={MAX_TIMES} />
            {resubmitError && <p className="gp-error">{resubmitError}</p>}
            <button type="submit" className="gp-submit-btn" disabled={resubmitting}>
              {resubmitting ? "Sending…" : "Send new times →"}
            </button>
          </form>
        </div>
      );
    }

    // pending
    return (
      <div className="gr-card">
        <span className="gr-icon">⏳</span>
        <h1>Still with Pooja</h1>
        <p>She hasn't reviewed your suggested times yet — you'll get an email as soon as she has.</p>
        <div className="gr-options gr-options-static">
          {view.proposedTimes.map((t, i) => (
            <div className="gr-option gr-option-static" key={i}>{fmtDateTime(t)}</div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <SEO title="Your guidance session — The PP Studio" description="Respond to your guidance session request." noIndex />
      <Navbar />
      <main className="gr-main">{renderBody()}</main>
      <Footer />
    </>
  );
}
